import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs';

import { ParticularOrderDto } from '../model/particularOrderDto';
import { CreateParticularOrderDto } from '../model/createParticularOrderDto';
import { UpdateParticularOrderDto } from '../model/updateParticularOrderDto';
import { CalculateDeliveryCostRequest } from '../model/calculateDeliveryCostRequest';
import { CalculateDeliveryCostResponse } from '../model/calculateDeliveryCostResponse';
import { WaypointCompletionTime } from '../model/waypointCompletionTime';

import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';

@Injectable({
  providedIn: 'root',
})
export class ParticularOrderService {
  protected basePath = '/';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration
  ) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  public createParticularOrder(
    body: CreateParticularOrderDto,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<ParticularOrderDto> {
    if (body === null || body === undefined) {
      throw new Error(
        'Required parameter body was null or undefined when calling createParticularOrder.'
      );
    }
    const headers = this.defaultHeaders;
    return this.httpClient.post<ParticularOrderDto>(
      `${this.basePath}/api/ParticularOrder/Post`,
      body,
      {
        headers,
        observe,
        reportProgress,
      }
    );
  }

  public getParticularOrderById(
    id: number,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<ParticularOrderDto> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling getParticularOrderById.'
      );
    }
    const headers = this.defaultHeaders;
    const params = { id: String(id) };
    return this.httpClient.get<ParticularOrderDto>(
      `${this.basePath}/api/ParticularOrder/GetById`,
      {
        headers,
        params,
        observe,
        reportProgress,
      }
    );
  }

  public updateParticularOrder(
    id: number,
    body: UpdateParticularOrderDto,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<ParticularOrderDto> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling updateParticularOrder.'
      );
    }
    if (body === null || body === undefined) {
      throw new Error(
        'Required parameter body was null or undefined when calling updateParticularOrder.'
      );
    }
    const headers = this.defaultHeaders;
    return this.httpClient.put<ParticularOrderDto>(
      `${this.basePath}/api/ParticularOrder/Put`,
      body,
      {
        headers,
        observe,
        reportProgress,
      }
    );
  }

  public deleteParticularOrder(
    id: number,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<void> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling deleteParticularOrder.'
      );
    }
    const headers = this.defaultHeaders;
    const params = { id: String(id) };
    return this.httpClient.delete<void>(
      `${this.basePath}/api/ParticularOrder/Delete`,
      {
        headers,
        params,
        observe,
        reportProgress,
      }
    );
  }

  public listParticularOrders(
    date?: Date | string,
    status?: string,
    recipientName?: string,
    deliveryId?: number,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<ParticularOrderDto[]> {
    const headers = this.defaultHeaders;
    let params = {} as any;
    if (date !== undefined && date !== null) {
      params.date = date instanceof Date ? date.toISOString() : date;
    }
    if (status !== undefined && status !== null) {
      params.status = status;
    }
    if (recipientName !== undefined && recipientName !== null) {
      params.recipientName = recipientName;
    }
    if (deliveryId !== undefined && deliveryId !== null) {
      params.deliveryId = deliveryId;
    }
    return this.httpClient.get<ParticularOrderDto[]>(
      `${this.basePath}/api/ParticularOrder/GetAll`,
      {
        headers,
        params,
        observe,
        reportProgress,
      }
    );
  }

  /**
   * Obtiene las liquidaciones de particulares por delivery y fecha
   * @param deliveryId
   * @param dtFrom
   * @param dtTo
   * @param number
   */
  public getParticularOrdersByDeliveryAndDate(
    deliveryId: number,
    dtFrom?: Date,
    dtTo?: Date,
    number?: number,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): import('rxjs').Observable<import('../model/particularOrderForDeliveryPayingDto').ParticularOrderForDeliveryPayingDto> {
    const headers = this.defaultHeaders;
    let params: any = { cdDelivery: deliveryId };
    if (dtFrom) params.dtFrom = dtFrom instanceof Date ? dtFrom.toISOString() : dtFrom;
    if (dtTo) params.dtTo = dtTo instanceof Date ? dtTo.toISOString() : dtTo;
    if (number !== undefined && number !== null) params.number = number;
    return this.httpClient.get<import('../model/particularOrderForDeliveryPayingDto').ParticularOrderForDeliveryPayingDto>(
      `${this.basePath}/api/ParticularOrder/GetParticularOrdersByDeliveryAndDate`,
      {
        headers,
        params,
        observe,
        reportProgress,
      }
    );
  }

  public calculateCost(
    body: CalculateDeliveryCostRequest
  ): Observable<CalculateDeliveryCostResponse> {
    if (body === null || body === undefined) {
      throw new Error(
        'Required parameter body was null or undefined when calling calculateCost.'
      );
    }

    const headers = this.defaultHeaders;

    return this.httpClient.post<CalculateDeliveryCostResponse>(
      `${this.basePath}/api/ParticularOrder/CalculateCost`,
      body,
      { headers }
    );
  }

  public assignCadet(
    body: { deliveryId:number,
    particularOrderId: number,
    adminId: number,
    assignedAt: Date},
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<number> {
    if (body === null || body === undefined) {
      throw new Error(
        'Required parameter body was null or undefined when calling assignCadet.'
      );
    }
    
    const headers = this.defaultHeaders;
    const params = {};
    
    return this.httpClient.post<number>(
      `${this.basePath}/api/DeliveryAssignment/Create`,
      body,
      {
        headers,
        params,
        observe,
        reportProgress,
      }
    );
  }

  public calculatePrice(
    body: CalculateDeliveryCostRequest
  ): Observable<CalculateDeliveryCostResponse> {
    if (body === null || body === undefined) {
      throw new Error(
        'Required parameter body was null or undefined when calling calculatePrice.'
      );
    }

    const headers = this.defaultHeaders;

    return this.httpClient.post<CalculateDeliveryCostResponse>(
      `${this.basePath}/api/ParticularOrder/CalculatePrice`,
      body,
      { headers }
    );
  }

  public getWaypointCompletionTimesByParticularOrder(
    particularOrderId: number
  ): Observable<WaypointCompletionTime[]> {
    if (particularOrderId === null || particularOrderId === undefined) {
      throw new Error(
        'Required parameter particularOrderId was null or undefined when calling getWaypointCompletionTimesByParticularOrder.'
      );
    }

    const headers = this.defaultHeaders;

    return this.httpClient.get<WaypointCompletionTime[]>(
      `${this.basePath}/api/Waypoint/GetWaypointCompletionTimesByParticularOrder?particularOrderId=${particularOrderId}`,
      { headers }
    );
  }
}
